/**
 * Dev-only mock adapter — intercepts every API call and returns fake data.
 * Activated when VITE_MOCK=true in .env.
 *
 * Login credentials:  sandeep@veytra.io / 12345
 */

import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

// ── Demo vehicle — stored in localStorage, advances every 10 s ────────────────

const DEMO_VEH_ID    = 'veh-demo'
const DEMO_STORE_KEY = 'demo_vehicle'       // vehicle info
const DEMO_START_KEY = 'demo_journey_start' // journey start epoch ms
const DEMO_TICK_MS   = 10_000              // 10 s per waypoint

/** 24-point real journey for RJ18CD7761 with per-point status metadata. */
const DEMO_WAYPOINTS: Array<{
  lat: number; lng: number
  speed: number; ignition: boolean; status: 'moving' | 'idle' | 'stopped'
}> = [
  { lat: 28.54478934204106, lng: 77.2014744734813,  speed: 0,  ignition: true,  status: 'moving'  }, // 0 start
  { lat: 28.54805023085492, lng: 77.20312671423447, speed: 38, ignition: true,  status: 'moving'  }, // 1
  { lat: 28.55082096446528, lng: 77.20424251319722, speed: 42, ignition: true,  status: 'moving'  }, // 2
  { lat: 28.54995392839964, lng: 77.2058518883618,  speed: 36, ignition: true,  status: 'moving'  }, // 3
  { lat: 28.55005007706896, lng: 77.20619112112772, speed: 40, ignition: true,  status: 'moving'  }, // 4
  { lat: 28.54997458927663, lng: 77.20635071991484, speed: 35, ignition: true,  status: 'moving'  }, // 5
  { lat: 28.54962950153695, lng: 77.2062033979575,  speed: 0,  ignition: true,  status: 'idle'    }, // 6  idle 5 s
  { lat: 28.54945334405205, lng: 77.20613480702276, speed: 32, ignition: true,  status: 'moving'  }, // 7
  { lat: 28.54866000288041, lng: 77.2063651912071,  speed: 37, ignition: true,  status: 'moving'  }, // 8
  { lat: 28.54758365563487, lng: 77.20695253824196, speed: 41, ignition: true,  status: 'moving'  }, // 9
  { lat: 28.54692538826207, lng: 77.20701329828005, speed: 39, ignition: true,  status: 'moving'  }, // 10
  { lat: 28.54540424111675, lng: 77.20718545171857, speed: 43, ignition: true,  status: 'moving'  }, // 11
  { lat: 28.54383859305354, lng: 77.20711456500746, speed: 38, ignition: true,  status: 'moving'  }, // 12
  { lat: 28.54368736445478, lng: 77.20867407265176, speed: 0,  ignition: true,  status: 'idle'    }, // 13 idle 7 s
  { lat: 28.54436344356486, lng: 77.20878546605493, speed: 0,  ignition: false, status: 'stopped' }, // 14 stop 10 s
  { lat: 28.54793947410986, lng: 77.20965635992569, speed: 44, ignition: true,  status: 'moving'  }, // 15
  { lat: 28.54910477124395, lng: 77.21005130017328, speed: 40, ignition: true,  status: 'moving'  }, // 16
  { lat: 28.55093719719186, lng: 77.2107500405907,  speed: 37, ignition: true,  status: 'moving'  }, // 17
  { lat: 28.55170218109456, lng: 77.21108422080017, speed: 42, ignition: true,  status: 'moving'  }, // 18
  { lat: 28.55138195595109, lng: 77.21221840817786, speed: 39, ignition: true,  status: 'moving'  }, // 19
  { lat: 28.55104393946540, lng: 77.21344373561266, speed: 0,  ignition: false, status: 'stopped' }, // 20 stop 12 s
  { lat: 28.55062586494269, lng: 77.21411209603166, speed: 35, ignition: true,  status: 'moving'  }, // 21
  { lat: 28.55035900799649, lng: 77.21408171601261, speed: 33, ignition: true,  status: 'moving'  }, // 22
  { lat: 28.54954064247801, lng: 77.21349436897775, speed: 0,  ignition: false, status: 'stopped' }, // 23 final stop
]

/** Haversine distance in metres between two lat/lng points. */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** Total distance in km for the first `upToIdx` waypoints. */
function demoDistanceKm(upToIdx: number): number {
  let m = 0
  for (let i = 1; i <= upToIdx; i++) {
    const a = DEMO_WAYPOINTS[i - 1], b = DEMO_WAYPOINTS[i]
    m += haversineMeters(a.lat, a.lng, b.lat, b.lng)
  }
  return Math.round(m / 10) / 100
}

/** Called once on module load — persists vehicle metadata & journey start. */
function initDemoVehicle(): void {
  localStorage.setItem(DEMO_STORE_KEY, JSON.stringify({
    name:                'RJ18CD7761',
    registration_number: 'RJ18CD7761',
    device_serial:       '78458789565478',
    imei:                '985642544515414',
    tag:                 'Sandeep',
  }))
  if (!localStorage.getItem(DEMO_START_KEY)) {
    localStorage.setItem(DEMO_START_KEY, String(Date.now()))
  }
}

initDemoVehicle()

/** Current waypoint index (0‥23), advancing every 10 s, clamped at end. */
function demoIdx(): number {
  const start = parseInt(localStorage.getItem(DEMO_START_KEY) ?? '0', 10) || Date.now()
  return Math.min(Math.floor((Date.now() - start) / DEMO_TICK_MS), DEMO_WAYPOINTS.length - 1)
}

// ── Mock vehicles ──────────────────────────────────────────────────────────────

const MOCK_VEHICLES = [
  { reg: 'DL 14ST 8900', name: 'Delhi Sedan 1',    type: 'Car',        color: 'White',  fuel: 'petrol',  lat: 28.54260279361263,   lng: 77.21984224100014,  speed: 45, heading: 90,  ignition: true  },
  { reg: 'HR 26GV 1549', name: 'Haryana Bike 1',   type: 'Motorcycle', color: 'Black',  fuel: 'petrol',  lat: 28.555543397974628,  lng: 77.20825949570964,  speed: 0,  heading: 0,   ignition: true  },
  { reg: 'HR 26 GV 4825',name: 'Haryana Scooter 1',type: 'Scooter',    color: 'Silver', fuel: 'petrol',  lat: 28.616219865101165,  lng: 77.0701823525515,   speed: 28, heading: 270, ignition: true  },
  { reg: 'GJ 05 RK 5694',name: 'Gujarat Truck 1',  type: 'Truck',      color: 'Blue',   fuel: 'diesel',  lat: 28.543673525095915,  lng: 76.71915437970057,  speed: 0,  heading: 180, ignition: false },
  { reg: 'RJ14CD5458',   name: 'Rajasthan Car 1',  type: 'Car',        color: 'Red',    fuel: 'petrol',  lat: 28.204080088403217,  lng: 75.41176714316187,  speed: 88, heading: 45,  ignition: true  },
  { reg: 'HR 26GV 1440', name: 'Haryana Bus 1',    type: 'Bus',        color: 'Yellow', fuel: 'diesel',  lat: 29.6225627031931,    lng: 78.40251750083323,  speed: 0,  heading: 0,   ignition: false },
  { reg: 'HR 38AF 7926', name: 'Haryana Pickup 1', type: 'Van',        color: 'Gray',   fuel: 'diesel',  lat: 28.98478128846374,   lng: 79.4007026226577,   speed: 31, heading: 135, ignition: true  },
  { reg: 'DL 12SJ 8257', name: 'Delhi Van 1',      type: 'Van',        color: 'White',  fuel: 'petrol',  lat: 29.383067915189386,  lng: 79.46432917785073,  speed: 0,  heading: 90,  ignition: false },
].map((v, i) => ({
  id:                  `veh-${String(i + 1).padStart(3, '0')}`,
  domain_id:           'dom-001',
  vehicle_type_id:     `vt-${String(i + 1).padStart(3, '0')}`,
  name:                v.name,
  registration_number: v.reg,
  make:                'Maruti',
  model:               i % 2 === 0 ? 'Swift' : 'Ertiga',
  year:                2020 + (i % 5),
  color:               v.color,
  chassis_number:      null,
  engine_number:       null,
  fuel_type:           v.fuel as 'petrol' | 'diesel' | 'cng',
  is_active:           true,
  created_at:          '2024-01-01T00:00:00Z',
  updated_at:          new Date().toISOString(),
  vehicle_type:        { id: `vt-${String(i + 1).padStart(3, '0')}`, name: v.type },
  assigned_device:     { id: `dev-${i + 1}`, imei: `86840${String(i + 1).padStart(9, '0')}` },
  assigned_driver:     i < 5 ? { id: `drv-${i + 1}`, name: ['Ravi Kumar', 'Amit Singh', 'Sanjay Patel', 'Deepak Sharma', 'Vijay Gupta'][i] } : null,
  // store tracking data alongside for live status
  _lat: v.lat, _lng: v.lng, _speed: v.speed, _heading: v.heading, _ignition: v.ignition,
}))

// Demo vehicle appended — built at read-time so distance reflects current position
const DEMO_VEHICLE = {
  id:                  DEMO_VEH_ID,
  domain_id:           'dom-001',
  vehicle_type_id:     'vt-demo',
  name:                'RJ18CD7761',
  registration_number: 'RJ18CD7761',
  make:                'Maruti',
  model:               'Swift',
  year:                2023,
  color:               'White',
  chassis_number:      null,
  engine_number:       null,
  fuel_type:           'petrol' as const,
  is_active:           true,
  created_at:          '2024-01-01T00:00:00Z',
  updated_at:          new Date().toISOString(),
  vehicle_type:        { id: 'vt-demo', name: 'Car' },
  assigned_device:     { id: 'dev-demo', imei: '985642544515414' },
  assigned_driver:     { id: 'drv-demo', name: 'Sandeep' },
}

// ── Bearing helper ─────────────────────────────────────────────────────────────

function calcBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = Math.PI / 180
  const y = Math.sin((lng2 - lng1) * R) * Math.cos(lat2 * R)
  const x = Math.cos(lat1 * R) * Math.sin(lat2 * R) - Math.sin(lat1 * R) * Math.cos(lat2 * R) * Math.cos((lng2 - lng1) * R)
  return (Math.atan2(y, x) / R + 360) % 360
}

// ── Route waypoints for moving vehicles (12-14 pts, loop) ─────────────────────

const MOCK_ROUTES: Record<string, [number, number][]> = {
  // veh-001 · Delhi Car · Ho Chi Minh Marg → Panchsheel Park loop
  'veh-001': [
    [28.5426, 77.2198], [28.5426, 77.2240], [28.5426, 77.2278],
    [28.5426, 77.2316], [28.5426, 77.2354], [28.5400, 77.2354],
    [28.5374, 77.2354], [28.5348, 77.2354], [28.5348, 77.2278],
    [28.5348, 77.2198], [28.5374, 77.2198], [28.5400, 77.2198],
  ],
  // veh-003 · Haryana Scooter · Gurugram residential loop
  'veh-003': [
    [28.6162, 77.0702], [28.6162, 77.0662], [28.6162, 77.0622],
    [28.6162, 77.0582], [28.6130, 77.0582], [28.6098, 77.0582],
    [28.6066, 77.0582], [28.6066, 77.0622], [28.6066, 77.0662],
    [28.6066, 77.0702], [28.6098, 77.0702], [28.6130, 77.0702],
  ],
  // veh-005 · Rajasthan Car · Fatehpur highway loop
  'veh-005': [
    [28.2041, 75.4118], [28.2068, 75.4154], [28.2095, 75.4190],
    [28.2122, 75.4226], [28.2149, 75.4226], [28.2176, 75.4226],
    [28.2176, 75.4190], [28.2176, 75.4154], [28.2176, 75.4118],
    [28.2149, 75.4082], [28.2122, 75.4046], [28.2095, 75.4046],
    [28.2068, 75.4082], [28.2041, 75.4118],
  ],
  // veh-007 · Haryana Pickup · Moradabad–Rampur road loop
  'veh-007': [
    [28.9848, 79.4007], [28.9820, 79.4040], [28.9792, 79.4073],
    [28.9764, 79.4106], [28.9736, 79.4106], [28.9708, 79.4106],
    [28.9708, 79.4073], [28.9708, 79.4040], [28.9708, 79.4007],
    [28.9736, 79.3974], [28.9764, 79.3941], [28.9792, 79.3941],
    [28.9820, 79.3974], [28.9848, 79.4007],
  ],
}

// ── Dynamic live statuses — advances one waypoint every 15 s ──────────────────

const TICK_MS = 15_000

function getLiveStatuses() {
  const existing = MOCK_VEHICLES.map((v) => {
    const route = MOCK_ROUTES[v.id]
    if (route && v._ignition && v._speed > 5) {
      const idx     = Math.floor(Date.now() / TICK_MS) % route.length
      const nextIdx = (idx + 1) % route.length
      const [lat, lng]         = route[idx]
      const [nextLat, nextLng] = route[nextIdx]
      const heading = Math.round(calcBearing(lat, lng, nextLat, nextLng))
      return {
        vehicle_id: v.id, device_id: v.assigned_device?.id ?? null,
        lat, lng, speed: v._speed, heading, ignition: v._ignition,
        recorded_at: new Date().toISOString(),
        vehicle_name: v.name, registration_number: v.registration_number,
      }
    }
    return {
      vehicle_id: v.id, device_id: v.assigned_device?.id ?? null,
      lat: v._lat, lng: v._lng, speed: v._speed, heading: v._heading, ignition: v._ignition,
      recorded_at: new Date(Date.now() - Math.floor(Math.random() * 300_000)).toISOString(),
      vehicle_name: v.name, registration_number: v.registration_number,
    }
  })

  // Demo vehicle — 10 s tick, non-looping, per-waypoint speed/ignition
  const idx    = demoIdx()
  const wp     = DEMO_WAYPOINTS[idx]
  const nextWp = DEMO_WAYPOINTS[Math.min(idx + 1, DEMO_WAYPOINTS.length - 1)]
  const heading = Math.round(calcBearing(wp.lat, wp.lng, nextWp.lat, nextWp.lng))

  ;(existing as unknown[]).push({
    vehicle_id:          DEMO_VEH_ID,
    device_id:           'dev-demo',
    lat:                 wp.lat,
    lng:                 wp.lng,
    speed:               wp.speed,
    heading,
    ignition:            wp.ignition,
    recorded_at:         new Date().toISOString(),
    vehicle_name:        'RJ18CD7761',
    registration_number: 'RJ18CD7761',
    today_distance_km:   demoDistanceKm(idx),
    waypoint_index:      idx,
    total_waypoints:     DEMO_WAYPOINTS.length,
  })

  return existing
}

// ── Mock alerts ────────────────────────────────────────────────────────────────

const MOCK_ALERT_TYPES = [
  { id: 'at-1', name: 'Overspeeding',      priority: 1, is_active: true },
  { id: 'at-2', name: 'Geofence Entry',    priority: 2, is_active: true },
  { id: 'at-3', name: 'Ignition ON',       priority: 3, is_active: true },
  { id: 'at-4', name: 'Ignition OFF',      priority: 3, is_active: true },
  { id: 'at-5', name: 'Harsh Braking',     priority: 2, is_active: true },
  { id: 'at-6', name: 'SOS',               priority: 1, is_active: true },
]

const MOCK_ALERTS = [
  { veh: 0, type: 0, priority: 1, minsAgo: 3  },
  { veh: 4, type: 5, priority: 1, minsAgo: 7  },
  { veh: 2, type: 4, priority: 2, minsAgo: 15 },
  { veh: 1, type: 1, priority: 2, minsAgo: 22 },
  { veh: 6, type: 0, priority: 1, minsAgo: 31 },
  { veh: 3, type: 2, priority: 3, minsAgo: 45 },
  { veh: 5, type: 3, priority: 3, minsAgo: 58 },
  { veh: 7, type: 4, priority: 2, minsAgo: 75 },
].map((a, i) => ({
  id: i + 1,
  domain_id: 'dom-001',
  vehicle_id:           MOCK_VEHICLES[a.veh].id,
  vehicle_name:         MOCK_VEHICLES[a.veh].name,
  registration_number:  MOCK_VEHICLES[a.veh].registration_number,
  alert_type_id: MOCK_ALERT_TYPES[a.type].id,
  alert_type: MOCK_ALERT_TYPES[a.type],
  priority: a.priority,
  is_read: false,
  triggered_at: new Date(Date.now() - a.minsAgo * 60_000).toISOString(),
  created_at: new Date(Date.now() - a.minsAgo * 60_000).toISOString(),
  message: null,
}))

// ── Mock trips ─────────────────────────────────────────────────────────────────

const MOCK_TRIPS = [
  { veh: 0, dist: 14800,  dur: 1320,  maxSpd: 68, avgSpd: 40, from: 'Connaught Place, New Delhi',  to: 'Nehru Place, New Delhi',    hrsAgo: 0.5  },
  { veh: 2, dist: 32500,  dur: 2100,  maxSpd: 82, avgSpd: 55, from: 'Gurugram Toll, NH-48',         to: 'Manesar Industrial Area',   hrsAgo: 1.2  },
  { veh: 4, dist: 88200,  dur: 5400,  maxSpd: 92, avgSpd: 58, from: 'Sikar, Rajasthan',             to: 'Jhunjhunu, Rajasthan',      hrsAgo: 2    },
  { veh: 6, dist: 22100,  dur: 1800,  maxSpd: 55, avgSpd: 44, from: 'Haldwani, Uttarakhand',        to: 'Nainital Road, Kathgodam',  hrsAgo: 3.5  },
  { veh: 1, dist: 9600,   dur: 720,   maxSpd: 45, avgSpd: 48, from: 'Faridabad Sector 15',          to: 'Ballabhgarh',               hrsAgo: 5    },
].map((t, i) => ({
  id: `trp-${String(i + 1).padStart(3, '0')}`,
  domain_id: 'dom-001',
  vehicle_id: MOCK_VEHICLES[t.veh].id,
  device_id: null,
  driver_id: MOCK_VEHICLES[t.veh].assigned_driver?.id ?? null,
  start_lat: MOCK_VEHICLES[t.veh]._lat,
  start_lng: MOCK_VEHICLES[t.veh]._lng,
  end_lat: MOCK_VEHICLES[t.veh]._lat + 0.05,
  end_lng: MOCK_VEHICLES[t.veh]._lng + 0.05,
  start_address: t.from,
  end_address: t.to,
  started_at: new Date(Date.now() - t.hrsAgo * 3_600_000).toISOString(),
  ended_at: new Date(Date.now() - t.hrsAgo * 3_600_000 + t.dur * 1000).toISOString(),
  distance_meters: t.dist,
  duration_seconds: t.dur,
  max_speed: t.maxSpd,
  avg_speed: t.avgSpd,
  idle_duration_seconds: Math.floor(t.dur * 0.1),
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
}))

// ── Demo trip + track generators ──────────────────────────────────────────────

const _DEMO_DIST_M  = Math.round(demoDistanceKm(DEMO_WAYPOINTS.length - 1) * 1000)
const _DEMO_DUR_S   = (DEMO_WAYPOINTS.length - 1) * (DEMO_TICK_MS / 1000) // 230 s

const DEMO_TRIP = {
  id:                    'trp-demo',
  domain_id:             'dom-001',
  vehicle_id:            DEMO_VEH_ID,
  device_id:             'dev-demo',
  driver_id:             'drv-demo',
  start_lat:             DEMO_WAYPOINTS[0].lat,
  start_lng:             DEMO_WAYPOINTS[0].lng,
  end_lat:               DEMO_WAYPOINTS[DEMO_WAYPOINTS.length - 1].lat,
  end_lng:               DEMO_WAYPOINTS[DEMO_WAYPOINTS.length - 1].lng,
  start_address:         'Sector 4, Dwarka, New Delhi',
  end_address:           'Sector 10, Dwarka, New Delhi',
  started_at:            new Date(Date.now() - 2 * 3_600_000).toISOString(),
  ended_at:              new Date(Date.now() - 2 * 3_600_000 + _DEMO_DUR_S * 1000).toISOString(),
  distance_meters:       _DEMO_DIST_M,
  duration_seconds:      _DEMO_DUR_S,
  max_speed:             44,
  avg_speed:             35,
  idle_duration_seconds: 32,
  created_at:            new Date().toISOString(),
  updated_at:            new Date().toISOString(),
}

/** Build track from the 24 demo waypoints with real timestamps (2 h ago). */
function generateDemoTrack() {
  const startMs = Date.now() - 2 * 3_600_000
  return DEMO_WAYPOINTS.map((wp, i) => ({
    lat:         wp.lat,
    lng:         wp.lng,
    speed:       wp.speed,
    ignition:    wp.ignition,
    heading:     i < DEMO_WAYPOINTS.length - 1
      ? Math.round(calcBearing(wp.lat, wp.lng, DEMO_WAYPOINTS[i + 1].lat, DEMO_WAYPOINTS[i + 1].lng))
      : 0,
    recorded_at: new Date(startMs + i * DEMO_TICK_MS).toISOString(),
  }))
}

/** Synthesise a curved track for any trip with start/end coords. */
function generateSyntheticTrack(trip: {
  start_lat: number; start_lng: number; end_lat: number; end_lng: number
  avg_speed: number; started_at: string; duration_seconds: number | null
}, numPts = 28) {
  const startMs = new Date(trip.started_at).getTime()
  const durMs   = (trip.duration_seconds ?? 1800) * 1000
  const { start_lat: sLat, start_lng: sLng, end_lat: eLat, end_lng: eLng } = trip
  const perpLat = -(eLng - sLng) * 0.4
  const perpLng =  (eLat - sLat) * 0.4

  return Array.from({ length: numPts }, (_, i) => {
    const t    = i / (numPts - 1)
    const dev  = Math.sin(t * Math.PI) * 0.012
    const lat  = sLat + (eLat - sLat) * t + perpLat * dev
    const lng  = sLng + (eLng - sLng) * t + perpLng * dev
    const t2   = Math.min(t + 1 / (numPts - 1), 1)
    const nlat = sLat + (eLat - sLat) * t2 + perpLat * Math.sin(t2 * Math.PI) * 0.012
    const nlng = sLng + (eLng - sLng) * t2 + perpLng * Math.sin(t2 * Math.PI) * 0.012
    const hdg  = i < numPts - 1 ? Math.round(calcBearing(lat, lng, nlat, nlng)) : 0
    const spd  = (i === 0 || i === numPts - 1)
      ? 0
      : Math.max(0, Math.round(trip.avg_speed * (0.5 + 0.5 * Math.sin(t * Math.PI)) + (Math.random() - 0.5) * 12))
    return { lat, lng, speed: spd, ignition: spd > 0, heading: hdg, recorded_at: new Date(startMs + t * durMs).toISOString() }
  })
}

// ── Mock user ──────────────────────────────────────────────────────────────────

const MOCK_USER = {
  id: 'usr-001',
  name: 'Sandeep Kumar',
  email: 'sandeep@veytra.io',
  phone: null,
  user_type_id: 'ut-001',
  domain_id: 'dom-001',
  parent_user_id: null,
  timezone: 'Asia/Kolkata',
  date_format: 'DD/MM/YYYY',
  is_active: true,
  last_login_at: new Date().toISOString(),
  created_at: '2024-01-01T00:00:00Z',
  updated_at: new Date().toISOString(),
}

const MOCK_USER_TYPE = {
  id: 'ut-001',
  name: 'Super Admin',
  is_system: true,
  is_active: true,
}

// All permission slugs — grant everything
const ALL_PERMISSION_SLUGS = [
  'vehicles.manage', 'devices.manage', 'sims.manage', 'drivers.manage',
  'geofences.manage', 'trips.view', 'alerts.view',
  'school.manage', 'field-force.manage', 'pets.manage', 'ev.manage',
  'dispatch.manage', 'billing.invoices', 'billing.payments', 'billing.plans',
  'reports.view', 'admin.users', 'admin.user-types', 'admin.alert-types',
  'admin.notifications', 'admin.payment-gateways', 'admin.domain',
]

const MOCK_PERMISSIONS = ALL_PERMISSION_SLUGS.map((slug, i) => ({
  id: `perm-${i}`,
  slug,
  module: slug.split('.')[0],
  action: slug.split('.')[1] ?? 'manage',
  can_read: true,
  can_create: true,
  can_update: true,
  can_delete: true,
}))

// ── Helpers ────────────────────────────────────────────────────────────────────

function ok(data: unknown, config: InternalAxiosRequestConfig): AxiosResponse {
  return { data: { success: true, data }, status: 200, statusText: 'OK', headers: {}, config }
}

function paginated(data: unknown[], config: InternalAxiosRequestConfig): AxiosResponse {
  return {
    data: {
      success: true,
      data,
      meta: { total: data.length, page: 1, per_page: 20, total_pages: Math.ceil(data.length / 20) || 1 },
    },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  }
}

// ── Router ─────────────────────────────────────────────────────────────────────

export function createMockAdapter(): AxiosAdapter {
  return async (config) => {
    // Small simulated network delay
    await new Promise(r => setTimeout(r, 120))

    const method = (config.method ?? 'get').toLowerCase()
    // Strip the base URL to get just the path
    const rawUrl = config.url ?? ''
    const url = rawUrl.replace(/^https?:\/\/[^/]+/, '').replace(/\?.*$/, '')

    // ── Auth ──────────────────────────────────────────────────────────────────

    if (method === 'post' && url === '/auth/login') {
      const body = JSON.parse(config.data ?? '{}')
      const login = (body.login ?? body.email ?? '').trim()
      const password = body.password ?? ''
      // Accept any non-empty login (email or phone) with password 12345
      if (login.length > 0 && password === '12345') {
        return ok({
          access_token: 'mock-access-token-dev',
          refresh_token: 'mock-refresh-token-dev',
          expires_in: 3600,
        }, config)
      }
      const err = Object.assign(new Error('Invalid credentials. Use password: 12345'), {
        isAxiosError: true,
        response: { data: { success: false, error: { code: 'INVALID_CREDENTIALS', message: 'Invalid credentials. Use password: 12345' } }, status: 401, statusText: 'Unauthorized', headers: {}, config },
        config,
        code: 'ERR_BAD_REQUEST',
      })
      return Promise.reject(err)
    }

    if (method === 'post' && url === '/auth/refresh') {
      return ok({ access_token: 'mock-access-token-dev', refresh_token: 'mock-refresh-token-dev', expires_in: 3600 }, config)
    }

    if (method === 'post' && url === '/auth/logout') {
      return ok({}, config)
    }

    if (method === 'post' && url === '/auth/forgot-password') {
      return ok({}, config)
    }

    // ── Users / Profile ───────────────────────────────────────────────────────

    if (method === 'get' && url === '/users/me') {
      return ok(MOCK_USER, config)
    }

    if (method === 'put' && url === '/users/me') {
      const body = JSON.parse(config.data ?? '{}')
      return ok({ ...MOCK_USER, ...body }, config)
    }

    if (method === 'get' && url.match(/^\/user-types\/[^/]+\/permissions$/)) {
      return ok(MOCK_PERMISSIONS, config)
    }

    if (method === 'get' && url === '/user-types') {
      return paginated([MOCK_USER_TYPE], config)
    }

    // ── Settings ──────────────────────────────────────────────────────────────

    if (url === '/settings') {
      return ok({
        timezone: 'Asia/Kolkata',
        date_format: 'DD/MM/YYYY',
        distance_unit: 'km',
        speed_unit: 'kmh',
        currency: 'INR',
        idle_threshold_minutes: 5,
        trip_min_distance_meters: 100,
        overspeed_threshold_kmh: 80,
        data_retention_days: 365,
      }, config)
    }

    // ── Live status (dashboard mini-map + live map) ────────────────────────────

    if (method === 'get' && url === '/vehicles/status/all') {
      return ok(getLiveStatuses(), config)
    }

    if (method === 'get' && url === '/tracking/live') {
      return ok(getLiveStatuses(), config)
    }

    if (method === 'get' && url.match(/^\/vehicles\/[^/]+\/live$/)) {
      const vehicleId = url.split('/')[2]
      const st = getLiveStatuses().find((s) => s.vehicle_id === vehicleId)
      return ok(st ?? null, config)
    }

    if (method === 'get' && url.match(/^\/vehicles\/veh-demo$/)) {
      return ok({ ...DEMO_VEHICLE, today_distance_km: demoDistanceKm(demoIdx()) }, config)
    }

    // ── Vehicles ──────────────────────────────────────────────────────────────

    if (method === 'get' && url === '/vehicles') {
      const params = config.params as Record<string, string> | undefined
      const search = (params?.search ?? '').toLowerCase()
      const base = MOCK_VEHICLES.map(({ _lat: _l, _lng: _g, _speed: _s, _heading: _h, _ignition: _i, ...v }) => v)

      // Append demo vehicle with live distance
      const curIdx = demoIdx()
      const allVehicles: unknown[] = [...base, {
        ...DEMO_VEHICLE,
        today_distance_km: demoDistanceKm(curIdx),
        week_distance_km:  demoDistanceKm(curIdx),
      }]
      let list = allVehicles as typeof base

      if (search) list = list.filter((v) => v.name.toLowerCase().includes(search) || v.registration_number.toLowerCase().includes(search))
      return paginated(list, config)
    }

    if (method === 'get' && url.match(/^\/vehicles\/[^/]+$/) && !url.includes('/trips') && !url.includes('/live')) {
      const vehicleId = url.split('/')[2]
      if (vehicleId === DEMO_VEH_ID) return ok({ ...DEMO_VEHICLE, today_distance_km: demoDistanceKm(demoIdx()) }, config)
      const v = MOCK_VEHICLES.find((v) => v.id === vehicleId)
      if (v) {
        const { _lat: _l, _lng: _g, _speed: _s, _heading: _h, _ignition: _i, ...clean } = v
        return ok(clean, config)
      }
    }

    // ── Trips ─────────────────────────────────────────────────────────────────

    const ALL_TRIPS = [...MOCK_TRIPS, DEMO_TRIP]

    if (method === 'get' && url === '/trips') {
      return paginated(ALL_TRIPS, config)
    }

    if (method === 'get' && url.match(/^\/vehicles\/[^/]+\/trips$/)) {
      const vehicleId = url.split('/')[2]
      return paginated(ALL_TRIPS.filter((t) => t.vehicle_id === vehicleId), config)
    }

    if (method === 'get' && url.match(/^\/trips\/[^/]+\/track$/)) {
      const tripId = url.split('/')[2]
      if (tripId === 'trp-demo') return ok(generateDemoTrack(), config)
      const trip = MOCK_TRIPS.find((t) => t.id === tripId)
      return ok(trip ? generateSyntheticTrack(trip) : [], config)
    }

    if (method === 'get' && url.match(/^\/trips\/[^/]+$/) && !url.includes('/track')) {
      const tripId = url.split('/')[2]
      return ok(ALL_TRIPS.find((t) => t.id === tripId) ?? null, config)
    }

    // ── Alerts ────────────────────────────────────────────────────────────────

    if (method === 'get' && url === '/alerts') {
      return paginated(MOCK_ALERTS, config)
    }

    if (method === 'get' && url === '/alerts/unread-count') {
      return ok({ count: MOCK_ALERTS.filter((a) => !a.is_read).length }, config)
    }

    if (method === 'post' && url === '/alerts/mark-read') {
      return ok({}, config)
    }

    // ── Alert types ───────────────────────────────────────────────────────────

    if (method === 'get' && url === '/alert-types') {
      return paginated(MOCK_ALERT_TYPES, config)
    }

    // ── Billing subscription (SubscriptionsPage) ──────────────────────────────

    if (method === 'get' && url === '/billing/subscription') {
      return ok({
        id: 'sub-001',
        plan: {
          id: 'plan-001', name: 'Pro', billing_cycle_months: 1,
          unit_type: 'vehicle', price_per_unit: 499, currency: 'INR', is_active: true, created_at: new Date().toISOString(),
        },
        status: 'active',
        renews_at: new Date(Date.now() + 30 * 86400000).toISOString(),
        unit_count: 12,
      }, config)
    }

    // ── Wallet ────────────────────────────────────────────────────────────────

    if (method === 'get' && url === '/billing/wallet') {
      return ok({ balance: 2450.00, currency: 'INR' }, config)
    }

    // ── Generic fallbacks ─────────────────────────────────────────────────────

    // All other GETs return empty paginated list
    if (method === 'get') {
      return paginated([], config)
    }

    // POST / PUT / DELETE → success
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      return ok({}, config)
    }

    return ok({}, config)
  }
}
